<script setup lang="ts" generic="ModelValueType extends string | number | boolean | undefined">
import UiRadioButton from '@/components/UIElements/UiRadio/UiRadioButton.vue';
import type { ElRadioGroup } from 'element-plus';

interface OptionsParams {
  label: string;
  value?: ModelValueType;
  disabled?: boolean;
}

interface Props {
  label?: string,
  hint?: string,
  disabled?: boolean,
  buttonClassName?: string,
  groupClassName?: string,
  direction?: 'row' | 'col',
  options?: OptionsParams[],
}

interface Emits<T> {
  'change': [value: T],
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  hint: '',
  direction: 'row',
  buttonClassName: '',
  groupClassName: '',
  disabled: false,
  options: () => [],
});

const emit = defineEmits<Emits<ModelValueType>>();
const modelValue = defineModel<ModelValueType>();

const handleChange = async (value: ModelValueType) => {
  emit('change', value);
};
</script>

<template>
  <div
    :class="[
      'ui-radio-group t:space-y-8',
      `ui-radio-group_${direction}`,
    ]"
  >
    <UiLabel
      v-if="label"
      :disabled="disabled"
      :label="label"
      :hint="hint"
    />
    <ElRadioGroup
      v-model="modelValue"
      :disabled="disabled"
      :class="[
        'ui-radio-group__radio-group ',
        { 't:space-y-8': direction === 'col' },
        { 't:space-x-16': direction === 'row' },
        groupClassName
      ]"
      @change="handleChange($event as ModelValueType)"
    >
      <slot name="buttons">
        <UiRadioButton
          v-for="(opt, id) in options"
          :key="id"
          :value="opt.value"
          :text="opt.label"
          :disabled="disabled || opt.disabled"
          :class="buttonClassName"
        />
      </slot>
    </ElRadioGroup>
    <slot />
  </div>
</template>

<style lang="scss">
.ui-radio-group {
  display: flex;
  flex-direction: column;
  line-height: normal;
  &_col &{
    &__radio-group{
      flex-direction: column;
      align-items: flex-start;
    }
  }
}
</style>
